import DOMPurify from "dompurify";

import { DEEP_LINK_BASE } from "@modules/common/constants";
import {
  RecommendationContext,
  RecommendationContextInput,
} from "gql/graphql";
import { getPlatformFromCookie } from "hooks/useInAppLogin";

// Marketplace copy is CMS-driven and may carry inline formatting markup
// (e.g. "<sup>1</sup> Based on a study…"). Only harmless inline tags are
// allowed; everything else (scripts, links, attributes) is removed.
const ALLOWED_INLINE_TAGS = ["sup", "sub", "b", "strong", "i", "em", "u", "br"];

export const sanitizeMarketplaceHtml = (html: string): string =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ALLOWED_INLINE_TAGS,
    ALLOWED_ATTR: [],
  });

/**
 * Wraps an ad's target URL in an AUTHENTICATED_URL deeplink so the
 * marketplace destination opens in its own webview.
 *
 * Android gets `titleSource=hidden` so the native AppBar is suppressed —
 * buyflow pages already render their own in-page header on Android.
 * iOS keeps `titleSource=fromUrl&title=` so the native nav bar (back + close)
 * is shown — buyflow pages hide their in-page header on iOS.
 */
export const buildMarketplaceDeeplink = (targetUrl: string): string => {
  const titleParams = getPlatformFromCookie()?.toUpperCase() === "ANDROID"
    ? "titleSource=hidden"
    : "titleSource=fromUrl&title=";
  return `${DEEP_LINK_BASE}?open=AUTHENTICATED_URL&${titleParams}&url=${encodeURIComponent(
    targetUrl
  )}`;
};

/**
 * Converts a recommendation context into the mutation's input type.
 */
export const buildRecommendationContextInput = (
  recommendationContext: RecommendationContext | null | undefined
): RecommendationContextInput | undefined => {
  if (!recommendationContext) {
    return undefined;
  }

  const { __typename, ...input } = recommendationContext;
  return input;
};
